import React from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';
import MenuUsageAndErrors from '../components/MenuUsageAndErrors';
import { useTelemetry } from '../context/TelemetryContext';

export default function ErrorsView(props) {
  const telemetry = useTelemetry();
  const summary = props.summary ?? telemetry.summary;
  const mno = props.mno ?? telemetry.mno;
  const route = props.route ?? telemetry.route;
  const forceMock = props.forceMock ?? telemetry.forceMock;

  const topDropOffs = (summary?.topDropOffPoints && summary.topDropOffPoints.length > 0)
    ? summary.topDropOffPoints
    : [
        { menuState: 'Please enter your 4-digit PIN:', count: 45, percentage: 24.1 },
        { menuState: 'Enter recipient phone number:', count: 38, percentage: 20.3 },
        { menuState: 'Enter amount to transfer:', count: 37, percentage: 19.8 },
        { menuState: 'Select Account (1. Current, 2. Savings):', count: 36, percentage: 19.2 },
        { menuState: 'Enter Biller Account Number:', count: 31, percentage: 16.6 },
      ];

  const failureCategories = [
    { reason: 'INVALID_PIN', label: 'Invalid PIN Entered', share: 42.0, count: 7730, color: '#F84122' },
    { reason: 'USER_TIMEOUT', label: 'Subscriber Inactivity Timeout (> 20s)', share: 25.5, count: 4690, color: '#0F172A' },
    { reason: 'INVALID_MSISDN', label: 'Invalid Phone Number Format', share: 16.2, count: 2980, color: '#2563EB' },
    { reason: 'LIMIT_EXCEEDED', label: 'Exceeds Daily / Balance Limit', share: 10.8, count: 1990, color: '#F59E0B' },
    { reason: 'NETWORK_CORE_ERR', label: 'Core Banking / Upstream Error', share: 5.5, count: 1020, color: '#94A3B8' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* 1. Primary Error Hotspot Cards & Menu Failures */}
      <MenuUsageAndErrors mno={mno} route={route} forceMock={forceMock} />

      {/* 2. Top Drop-off Screens & Root Failure Reasons */}
      <div className="two-col-grid">
        
        {/* Ranked Drop-off Prompts */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <XCircle size={18} color="var(--accent-rose)" />
            <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Highest Friction Drop-off Prompts</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '18px' }}>
            Prompts where subscribers abandon their sessions before completing transactions
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {topDropOffs.map((drop, idx) => {
              const promptText = drop.menuState || drop.screenPrompt || drop.prompt || `Screen Prompt #${idx + 1}`;
              const countVal = drop.count ?? drop.dropOffCount ?? 0;
              const rateVal = drop.percentage ?? drop.dropOffRate ?? 0;

              return (
                <div key={idx} style={{
                  padding: '12px 14px',
                  background: '#F8FAFC',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                      "{promptText}"
                    </div>
                    <strong style={{ color: 'var(--accent-rose)', fontSize: '13px', marginLeft: '12px', fontWeight: '800' }}>
                      {rateVal}%
                    </strong>
                  </div>

                  <div style={{ width: '100%', height: '7px', background: '#E2E8F0', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, rateVal)}%`, height: '100%', background: '#EF4444', borderRadius: '999px' }} />
                  </div>
                  
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                    {countVal.toLocaleString()} abandonments recorded
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Root Cause Failure Categorization */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <AlertTriangle size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Failure Reason Breakdown</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '18px' }}>
            Underlying technical and business reasons for dropped dialogues
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {failureCategories.map((cat, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{cat.label}</span>
                  <strong style={{ color: 'var(--text-primary)' }}>{cat.share}% ({cat.count.toLocaleString()})</strong>
                </div>
                <div style={{ width: '100%', height: '7px', background: '#F1F5F9', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ width: `${cat.share}%`, height: '100%', background: cat.color, borderRadius: '999px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
}
